import React, { useState, useEffect, useCallback } from 'react';
import { syncApi } from '../../services/api';
import DevOnlyGuard from '../../components/common/DevOnlyGuard';
import syncStatusBadge from './syncStatusBadge';
import {
  Eye, Play, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, RotateCcw, AlertTriangle, Loader2
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

const ViewSyncPage = () => {
  const [targets, setTargets]               = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [history, setHistory]               = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  const [environment, setEnvironment] = useState('preprod');
  const [availability, setAvailability] = useState(null);

  const [syncing, setSyncing]     = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  // ── Data loading ───────────────────────────────────────────────────
  const loadHistory = useCallback(async (target) => {
    if (!target) return;
    setHistoryLoading(true);
    try {
      const res = await syncApi.getViewHistory(target);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    syncApi.getAvailability()
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability({ available: false, reason: 'Unable to reach the sync service.', environments: [], phases: [] }));
  }, []);

  // Target list comes from the SELECTED environment's admin DB. Previously this always
  // read the local one, so prod was unreachable and its views could only be updated by
  // hand in DBeaver.
  useEffect(() => {
    setSelectedTarget(null);
    setSyncResult(null);
    syncApi.getViewTargets(environment)
      .then(res => setTargets(res.data || []))
      .catch(() => setTargets([]));
  }, [environment]);

  useEffect(() => {
    if (selectedTarget) loadHistory(selectedTarget);
  }, [selectedTarget, loadHistory]);

  // A prod acknowledgement covers one target + environment only; any change invalidates it.
  useEffect(() => { setAcknowledged(false); }, [selectedTarget, environment]);

  // selectedTarget is a slug; everything user-facing shows DisplayName.
  const targetLabelFor = (slug) =>
    targets.find(t => (t.slug || t.connStringName) === slug)?.displayName || slug;

  // ── Actions ────────────────────────────────────────────────────────
  const runSync = async () => {
    if (!selectedTarget || !canSync) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await syncApi.syncViews(selectedTarget, environment);
      setSyncResult({ success: true, data: res.data });
      loadHistory(selectedTarget);
    } catch (err) {
      setSyncResult({ success: false, error: err.response?.data?.error || err.message });
    }
    setSyncing(false);
  };

  const acknowledge = () => { setSyncResult(null); setAcknowledged(false); };

  // ── Helpers ────────────────────────────────────────────────────────
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const isComplete      = !!syncResult;
  const showExecuteTile = !!selectedTarget;
  const showResultsTile = isComplete && syncResult.success;
  const hasFailures     = syncResult?.data?.totalFail > 0;
  // View sync has no dry run, so every Prod run needs the acknowledgement.
  const isProdTarget    = environment === 'prod';
  const canSync         = !syncing && (!isProdTarget || acknowledged);

  // ── Render ─────────────────────────────────────────────────────────
  const targetLabel = targetLabelFor(selectedTarget);

  // Block the whole section, not just the tiles. Same treatment as the other dev-only pages.
  if (availability && !availability.available) {
    return <DevOnlyGuard pageName="View Sync" message={availability.reason} />;
  }

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Eye size={20} className="stui-imgupload-title-icon" />
            View Synchronization
          </h1>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="stui-steps-layout">

          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="stui-step-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">
              Select a target database. Views sync from <strong>TMPLT</strong> master.
            </p>

            <div className="stui-form-field">
              <label className="stui-label-xs stui-mb-xs" htmlFor="viewsync-env-select">TARGET ENVIRONMENT</label>
              <StuiSelect
                id="viewsync-env-select"
                className="stui-field-input"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                disabled={syncing}
              >
                <option value="preprod">PreProd</option>
                <option value="prod">Prod</option>
              </StuiSelect>
            </div>

            <div className="stui-form-field">
              <label className="stui-label-xs stui-mb-xs" htmlFor="viewsync-target-select">TARGET DATABASE</label>
              <StuiSelect
                id="viewsync-target-select"
                className="stui-field-input"
                value={selectedTarget || ''}
                onChange={(e) => { setSelectedTarget(e.target.value || null); setSyncResult(null); }}
                disabled={syncing}
              >
                <option value="">— Select database —</option>
                {targets.map(t => (
                  // value is the SLUG (a stable identifier the backend can resolve); the
                  // label is DisplayName. These were previously the same field, which is
                  // what broke GF and T1 Energy.
                  <option key={t.slug || t.connStringName} value={t.slug || t.connStringName}>
                    {t.displayName}
                  </option>
                ))}
              </StuiSelect>
            </div>
            {targets.length === 0 && (
              <span className="stui-loading">Loading targets...</span>
            )}

            {environment === 'prod' && (
              <div className="stui-inline-notice stui-inline-notice-warning">
                <AlertTriangle size={13} />
                <span>
                  Targets <strong>PRODUCTION</strong>. Running this replaces view definitions in
                  {' '}<strong>Prod_{targetLabel || '…'}</strong>.
                </span>
              </div>
            )}

            {selectedTarget && (
              <div>
                <div className="stui-subsection-divider" />
                <button
                  className="stui-btn-ghost"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="stui-mt-xs">
                    <table className="stui-inner-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Views</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{(formatTimestamp(h.started_At) || '--')}</td>
                            <td>
                              <span className={syncStatusBadge(h.status)}>
                                {h.status}
                              </span>
                            </td>
                            <td>{h.objects_Applied ?? '--'}</td>
                            <td>{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <div className="stui-text-italic stui-mt-xs">No view sync history for {targetLabel}.</div>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`stui-step-panel ${syncing ? 'running' : ''}`}>
            {showExecuteTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">2</span>
                  <h3>Execute</h3>
                </div>

                {!syncing && !isComplete && (
                  <>
                    <p className="stui-step-desc">
                      Pushes every view from <strong>TMPLT</strong> to <strong>{targetLabel}</strong>.
                    </p>
                    <button className="stui-btn stui-btn-primary" onClick={runSync} disabled={!canSync}>
                      <Play size={16} />
                      Sync Views
                    </button>

                    {/* Prod-target acknowledgement — required for every Prod run */}
                    {isProdTarget && (
                      <label className="stui-inline-notice stui-inline-notice-warning">
                        <input
                          type="checkbox"
                          className="stui-checkbox-input"
                          checked={acknowledged}
                          onChange={(e) => setAcknowledged(e.target.checked)}
                        />
                        <span>
                          I understand this replaces view definitions in{' '}
                          <strong>Prod_{targetLabel}</strong> in Azure Production.
                        </span>
                      </label>
                    )}
                  </>
                )}

                {syncing && (
                  <>
                    <div className="stui-row-8 stui-fw-semibold stui-text-primary">
                      <Loader2 size={18} className="stui-spinning" />
                      <span>Syncing views to {targetLabel}...</span>
                    </div>
                  </>
                )}

                {isComplete && (
                  <div className={`stui-inline-notice stui-inline-notice-${!syncResult.success || hasFailures ? 'error' : 'success'}`}>
                    {!syncResult.success || hasFailures
                      ? <XCircle size={18} className="stui-text-error" />
                      : <CheckCircle size={18} className="stui-td-success" />}
                    <div>
                      <div className="stui-fw-semibold">
                        {!syncResult.success
                          ? 'View Sync Failed'
                          : hasFailures
                            ? `View Sync — ${syncResult.data.totalFail} failure(s)`
                            : 'View Sync Complete'}
                      </div>
                      {syncResult.success && (
                        <div className="stui-text-xs stui-text-muted">
                          {syncResult.data.totalSuccess} of {syncResult.data.viewCount} views synced
                        </div>
                      )}
                      {!syncResult.success && (
                        <div className="stui-text-xs stui-text-muted">{syncResult.error}</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ═══════════ TILE 3: RESULTS ═══════════ */}
          <div className="stui-step-panel">
            {showResultsTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">3</span>
                  <h3>Results</h3>
                </div>

                <div className={`stui-inline-notice stui-inline-notice-${hasFailures ? 'error' : 'success'}`}>
                  {hasFailures ? <XCircle size={20} /> : <CheckCircle size={20} />}
                  <div className="stui-fw-semibold">
                    {hasFailures
                      ? `${syncResult.data.totalFail} view(s) failed — see below`
                      : `${syncResult.data.viewCount} views synced to ${syncResult.data.target}`}
                  </div>
                </div>

                <div className="stui-mt-sm">
                  <table className="stui-inner-table">
                    <thead>
                      <tr>
                        <th>View</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncResult.data.results.map((r, i) => (
                        <tr key={i}>
                          <td>{r.name}</td>
                          <td title={r.error || ''}>
                            {r.success
                              ? <CheckCircle size={14} className="stui-td-success" />
                              : <XCircle size={14} className="stui-text-error" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button className="stui-btn stui-btn-primary stui-ml-auto" onClick={acknowledge}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ViewSyncPage;
